// #ux-redesign-start: AddressCard — tarjeta compacta DS.
// Mobile-first: una columna ancha; en desktop la grilla del padre arma 2-3 col.
// Layout: header (badge + acciones icon-only) + body (firma multilínea).
// Edit dispara el Modal del Account (vía setAddressState({showForm:true})).
// Delete con ConfirmDialog del DS.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslate } from 'react-translate';

import { Card, Badge, Button, ConfirmDialog } from 'components/ds';
import { actionAddressDelete } from 'store/actions/profilesActions';

const SCOPE_BADGE = {
  company: { variant: 'success', i18nKey: 'company' },
  public: { variant: 'danger', i18nKey: 'public' },
  packstation: { variant: 'warning', i18nKey: 'dhl-packstation' },
};

const TYPE_META = {
  shipping: { icon: 'icon-shiping', i18nKey: 'shipping-address' },
  billing: { icon: 'icon-billing', i18nKey: 'billing-address' },
};

const AddressCard = ({ address, setAddressState }) => {
  const dispatch = useDispatch();
  const t = useTranslate('profile-address');
  const [deleteOpen, setDeleteOpen] = useState(false);

  const onConfirmDelete = () => {
    dispatch(actionAddressDelete(address._id));
    setDeleteOpen(false);
  };

  const onEdit = () => {
    setAddressState({ data: address, showForm: true });
  };

  const typeMeta = TYPE_META[address.type] || TYPE_META.billing;
  const scopeMeta = SCOPE_BADGE[address.scope];

  const isPackstation = address.scope === 'packstation';
  const street = isPackstation
    ? `${address.packstation || ''} ${address.packstationNumber || ''}`
    : `${address.street || ''} ${address.streetNumber || ''}`;
  const countryLabel =
    address.country && typeof address.country.label !== 'undefined'
      ? address.country.label
      : '';

  return (
    <Card padding="md" elevation={1} className="ds-address-card">
      <div className="ds-address-card__head">
        <div className="ds-address-card__title">
          <i className={`${typeMeta.icon} ds-address-card__type-icon`} aria-hidden="true" />
          <span>{t(typeMeta.i18nKey)}</span>
        </div>
        <div className="ds-address-card__actions">
          <Button
            variant="tertiary"
            size="sm"
            onClick={onEdit}
            name="btn-edit-address"
            aria-label={t('edit') || 'Bearbeiten'}
            iconLeft={<i className="fas fa-edit" />}
          />
          <Button
            variant="tertiary"
            size="sm"
            onClick={() => setDeleteOpen(true)}
            name="btn-delete-address"
            aria-label={t('btn-delete') || 'Löschen'}
            iconLeft={<i className="fas fa-trash-alt" />}
          />
        </div>
      </div>

      <div className="ds-address-card__body">
        <div className="ds-address-card__line ds-address-card__line--name">
          {`${address.firstName || ''} ${address.lastName || ''}`.trim()}
        </div>
        <div className="ds-address-card__line">{street.trim()}</div>
        <div className="ds-address-card__line">
          {address.postalCode} {address.postalCode && address.city ? '· ' : ''}{address.city}
        </div>
        {countryLabel && (
          <div className="ds-address-card__line ds-address-card__line--muted">
            {countryLabel}
          </div>
        )}
        {address.aditional && (
          <div className="ds-address-card__line ds-address-card__line--muted">
            {address.aditional}
          </div>
        )}
      </div>

      {(address.favourite || scopeMeta) && (
        <div className="ds-address-card__foot">
          {address.favourite && (
            <Badge variant="warning" size="sm">
              <i
                className="icon-star-active"
                aria-hidden="true"
                style={{ marginRight: 4 }}
              />
              {address.type === 'billing'
                ? t('favourite-for-billing')
                : t('favourite-for-shipping')}
            </Badge>
          )}
          {scopeMeta && (
            <Badge variant={scopeMeta.variant} size="sm">
              {t(scopeMeta.i18nKey)}
            </Badge>
          )}
        </div>
      )}

      <ConfirmDialog
        isOpen={deleteOpen}
        title={t('are-you-sure')}
        confirmLabel={t('btn-yes')}
        cancelLabel={t('btn-no')}
        variant="danger"
        onConfirm={onConfirmDelete}
        onCancel={() => setDeleteOpen(false)}
      />
    </Card>
  );
};

export default AddressCard;
// #ux-redesign-end
